import sortable from "html5sortable/dist/html5sortable.es";

// Setup
//    - Table rows needs to have a data-id attribute
//    - Table rows needs to have a data-ordering attribute
//    - Reorder Button text container needs to carry a selector of #reorder_button_text
//    - Reorder Button can have a spinner element with selector of #spinner
//    - Container that has the ordering number needs to have the selector of #ordering_number
//    - Container that has the ordering icon needs to have the selector of #ordering_icon
class Reordinator {
  // table: HTML Element, document.querySelector('..')
  // reorderButton: HTML Element, document.querySelector('..')
  // alertDialog: HTML Element, document.querySelector('..')
  // buttonsSelectorToDisableOnSort: string[], class names or ids of buttons to disable
  //                                 when the table is in sorting mode.
  // itemSerializer: () => any, pass to sortable, check out html5sortable documentations
  // processItemData: (row) => any, when on sort finish, after serialization from the library,
  //                  each row of data will be past to this function. Expects something in return.
  //                  optional.
  // onSortFinish: (serializedAndProcessedData) => void, function to call when the user finish
  //               reordering the table.
  constructor(
    table,
    reorderButton,
    alertDialog,
    buttonsSelectorToDisableOnSort,
    itemSerializer,
    processItemData,
    onSortFinish
  ) {
    this.table = table;
    this.reorderButton = reorderButton;
    this.alertDialog = alertDialog;
    this.buttonsSelectorToDisableOnSort = buttonsSelectorToDisableOnSort;
    this.itemSerializer = itemSerializer;
    this.processItemData = processItemData;
    this.onSortFinish = onSortFinish;

    this.sorting = false;
    this.loading = false;
  }

  toggleOrderingIcon = (enable) => {
    const orderingIcons = this.table.querySelectorAll("#ordering_icon");
    if (orderingIcons) {
      if (enable) {
        // We want to see the three layer icon on sorting mode.
        orderingIcons.forEach((element) => {
          element.classList.remove("d-none");
        });
      } else {
        // We do not want to see the three layer icon when not sorting.
        orderingIcons.forEach((element) => {
          element.classList.add("d-none");
        });
      }
    }
  };

  toggleOrderingNumber = (enable) => {
    const orderingNumbers = this.table.querySelectorAll("#ordering_number");
    if (orderingNumbers) {
      if (enable) {
        // We do not want to see the number of order
        // on sorting mode.
        orderingNumbers.forEach((element) => {
          element.classList.add("d-none");
        });
      } else {
        // We want to see the number of order
        // when not on sorting mode.
        orderingNumbers.forEach((element) => {
          element.classList.remove("d-none");
        });
      }
    }
  };

  // This adds a disabled attribute to buttons
  // on disable, relies on bootstrap to do this.
  toggleUserProvidedButtons = (disable) => {
    const { buttonsSelectorToDisableOnSort, table } = this;
    buttonsSelectorToDisableOnSort.forEach((buttonSelector) => {
      const buttons = table.querySelectorAll(buttonSelector);
      if (buttons) {
        buttons.forEach((button) => {
          if (disable) {
            button.setAttribute("disabled", disable);
            button.classList.add("disabled");
          } else {
            button.removeAttribute("disabled");
            button.classList.remove("disabled");
          }

          button.setAttribute("aria-disabled", disable);
        });
      }
    });
  };

  toggleSortable = (enable) => {
    const { table } = this;
    if (enable) {
      sortable(table, "enable");
      table.querySelectorAll("tr[data-id]").forEach((row) => {
        row.style.cursor = "grab";
      });
      table.addEventListener("sortstart", this.onSortStart);
      table.addEventListener("sortstop", this.onSortStop);
    } else {
      sortable(table, "disable");
      table.querySelectorAll("tr[data-id]").forEach((row) => {
        row.style.cursor = "";
      });
      table.removeEventListener("sortstart", this.onSortStart);
      table.removeEventListener("sortstop", this.onSortStop);
    }
  };

  onSortStart = () => {
    document.body.style.cursor = "grabbing";
    document.body.style.userSelect = "none";
  };

  onSortStop = () => {
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };

  getReorderButtonDefaultState = () => {
    return [
      false, // sorting
      false, // loading
      false, // disable
      '<i class="fa fa-bars me-2"></i> Reorder Sequence', // buttonText
    ];
  };

  getReorderButtonSortingState = () => {
    return [
      true, // sorting
      false, // loading
      false, // disable
      '<i class="fa fa-check me-2"></i> Finish reordering', // buttonText
    ];
  };

  getReorderButtonLoadingState = () => {
    return [
      false, // sorting
      true, // loading
      true, // disable
      'Updating', // buttonText
    ];
  };

  getNextReorderButtonState = () => {
    const {
      enable,
      loading,
      getReorderButtonDefaultState,
      getReorderButtonSortingState,
      getReorderButtonLoadingState,
    } = this;
    // Not sorting && loading state.
    if (!enable && !loading) {
      return getReorderButtonSortingState();
      // Sorting && not loading.
    } else if (enable && !loading) {
      return getReorderButtonLoadingState();
      // Sorting
    } else {
      return getReorderButtonDefaultState();
    }
  };

  setReorderButtonState = (enable, loading) => {
    this.enable = enable;
    this.loading = loading;
  };

  executeReorderButtonNextState = (loading, disable, buttonText) => {
    const { reorderButton } = this;
    const spinner = reorderButton.querySelector("#spinner");
    reorderButton.querySelector("#reorder_button_text").innerHTML = buttonText;

    if (disable) {
      reorderButton.setAttribute("disabled", true);
    } else {
      reorderButton.removeAttribute("disabled");
    }

    if (spinner && loading) {
      spinner.classList.remove("d-none");
    } else {
      spinner.classList.add("d-none");
    }
  };

  returnToDefaultState = () => {
    const {
      setReorderButtonState,
      executeReorderButtonNextState,
      toggleUserProvidedButtons,
      toggleOrderingIcon,
      toggleOrderingNumber,
    } = this;

    setReorderButtonState(false, false);
    executeReorderButtonNextState(false, false, '<i class="fa fa-bars me-2"></i> Reorder Sequence');
    toggleUserProvidedButtons(false);
    toggleOrderingNumber(false);
    toggleOrderingIcon(false);
    this.toggleSortable(false);
  };

  setAlertMessage = (status, message = null) => {
    const { alertDialog } = this;
    if (alertDialog) {
      if (message) {
        alertDialog.classList.remove("d-none");
        if (status === "success") {
          alertDialog.classList.add("alert-success");
          alertDialog.innerHTML = message;
        } else {
          alertDialog.classList.add("alert-danger");
          alertDialog.innerHTML = message;
        }
      } else {
        alertDialog.classList.add("d-none");
        alertDialog.classList.remove("alert-success");
        alertDialog.classList.remove("alert-danger");
      }
    }
  };

  setOrderings = (idToOrdering) => {
    const rows = this.table.querySelectorAll("tr[data-id]");
    rows.forEach((row) => {
      const { id } = row.dataset;
      const newOrdering = idToOrdering[parseInt(id)];

      if (newOrdering) {
        row.setAttribute("data-ordering", newOrdering);

        const orderingUnit = row.querySelector("#ordering_number");
        if (orderingUnit) {
          orderingUnit.innerHTML = newOrdering;
        }
      }
    });
  };

  onReorderButtonPress = (event) => {
    const {
      table,
      getNextReorderButtonState,
      setReorderButtonState,
      executeReorderButtonNextState,
      onSortFinish,
      toggleUserProvidedButtons,
      toggleOrderingNumber,
      toggleOrderingIcon,
      setAlertMessage,
      processItemData,
    } = this;

    const [enable, loading, disable, buttonText] = getNextReorderButtonState();
    setReorderButtonState(enable, loading);
    executeReorderButtonNextState(loading, disable, buttonText);

    this.toggleSortable(table, enable);

    // API call
    if (!enable && loading) {
      let data = sortable(table, "serialize");
      if (processItemData) {
        data = data[0].items.map(processItemData);
      }
      onSortFinish(data);
    } else if (enable && !loading) {
      toggleUserProvidedButtons(enable);
      toggleOrderingNumber(enable);
      toggleOrderingIcon(enable);
      setAlertMessage("hide", null);
    }
  };

  attach = () => {
    const { table, itemSerializer, reorderButton, onReorderButtonPress } = this;
    sortable(table, {
      placeholderClass: "transparent-purple-bg",
      itemSerializer: itemSerializer,
    });
    sortable(table, "disable");
    reorderButton.addEventListener("click", onReorderButtonPress);
  };

  detach = () => {
    const { reorderButton, onReorderButtonPress } = this;
    reorderButton.removeEventListener("click", onReorderButtonPress);
  };
}

export default Reordinator;
